/**
 * Transient acknowledgements, with an undo where the action allows one.
 *
 * There was no toast system: one screen animated a bespoke `.undo-toast` from
 * hand-written CSS, and everywhere else re-invented a `feedback` state and
 * rendered an inline alert that never went away.
 *
 * Only passive notices belong here. Anything the reader has to act on, an error
 * above all, stays inline where it cannot dismiss itself mid-sentence.
 *
 * @module components/ui/ToastProvider
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "./Button";
import { Icon } from "./Icon";

export interface ToastOptions {
  message: string;
  tone?: "info" | "success" | "warning";
  /** Offered beside the message. Dismisses the toast when pressed. */
  action?: { label: string; onAction: () => void };
  /** Defaults to 5s, or 15s when an action is offered so it can be reached. */
  durationMs?: number;
  /**
   * Used verbatim, so a caller that raises the same toast twice within a
   * duration owns making them distinguishable. Left unset, each gets
   * `toast-<n>`.
   */
  "data-testid"?: string;
}

interface ActiveToast extends ToastOptions {
  id: number;
  /** Kept so the timer can be restarted after a hover or a focus holds it. */
  duration: number;
}

type ShowToast = (options: ToastOptions) => void;

const ToastContext = createContext<ShowToast | null>(null);

/**
 * Show a transient notice.
 *
 * @returns A function that raises one toast.
 * @throws When called outside {@link ToastProvider}.
 */
export function useToast(): ShowToast {
  const show = useContext(ToastContext);
  if (!show) {
    throw new Error("useToast requires a ToastProvider above it in the tree.");
  }
  return show;
}

const TONE_ICON: Record<NonNullable<ToastOptions["tone"]>, string> = {
  info: "info",
  success: "check-circle",
  warning: "warning",
};

/** Mount once, at the shell root. */
export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  // Restartable, so hovering or focusing a toast can hold it open. An undo
  // that disappears while the user is tabbing to it takes their focus to the
  // body with it, and the action it offered is gone with no way back.
  const schedule = useCallback(
    (id: number, duration: number) => {
      const existing = timers.current.get(id);
      if (existing) {
        clearTimeout(existing);
      }
      timers.current.set(
        id,
        setTimeout(() => {
          dismiss(id);
        }, duration),
      );
    },
    [dismiss],
  );

  // Counted, not toggled. The pointer and the keyboard hold independently, so
  // releasing one while the other still holds must not restart the countdown:
  // a toast dismissed under a reader takes their focus to the body and the
  // undo with it.
  const holds = useRef(new Map<number, number>());

  const hold = useCallback((id: number) => {
    holds.current.set(id, (holds.current.get(id) ?? 0) + 1);
    const existing = timers.current.get(id);
    if (existing) {
      clearTimeout(existing);
      timers.current.delete(id);
    }
  }, []);

  const release = useCallback(
    (id: number, duration: number) => {
      const left = (holds.current.get(id) ?? 1) - 1;
      if (left > 0) {
        holds.current.set(id, left);
        return;
      }
      holds.current.delete(id);
      schedule(id, duration);
    },
    [schedule],
  );

  const show = useCallback<ShowToast>(
    (options) => {
      nextId.current += 1;
      const id = nextId.current;
      // An undo needs long enough to be noticed, read and reached; a bare
      // acknowledgement does not.
      const duration = options.durationMs ?? (options.action ? 15000 : 5000);
      setToasts((current) => [...current, { ...options, id, duration }]);
      schedule(id, duration);
    },
    [schedule],
  );

  useEffect(() => {
    const pending = timers.current;
    const held = holds.current;
    return () => {
      pending.forEach((timer) => {
        clearTimeout(timer);
      });
      pending.clear();
      held.clear();
    };
  }, []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Polite, not assertive: a toast never interrupts what is being read. */}
      {/* Known limitation: a `<dialog>` opened with showModal() is promoted to
          the top layer, which no z-index can reach, so a toast raised while a
          Modal is open paints behind it. `popover` would fix it but makes the
          strip display:none until showPopover() runs, so any environment where
          that call does not happen loses the toast entirely — a worse failure
          than the one it cures. A modal that needs to report something should
          say so inside itself. */}
      <div className="toast toast-bottom toast-center sm:toast-end z-[200]" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="alert max-w-[min(360px,calc(100vw-2rem))] gap-2 text-sm shadow-none"
            // Held while hovered or focused, restarted on the way out: the
            // dismissal removes the element a keyboard user is standing on,
            // which drops their focus to the body and takes the undo with it.
            onMouseEnter={() => {
              hold(toast.id);
            }}
            onMouseLeave={() => {
              release(toast.id, toast.duration);
            }}
            onFocus={() => {
              hold(toast.id);
            }}
            onBlur={() => {
              release(toast.id, toast.duration);
            }}
            // Suffixed with the id: two toasts up at once both matched a bare
            // "toast", which makes getByTestId throw and a Playwright locator
            // strict-mode ambiguous, on timing the suite does not control.
            data-testid={toast["data-testid"] ?? `toast-${String(toast.id)}`}
          >
            <Icon name={TONE_ICON[toast.tone ?? "info"]} aria-hidden />
            <span className="flex-1">{toast.message}</span>
            {toast.action && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  toast.action?.onAction();
                  dismiss(toast.id);
                }}
              >
                {toast.action.label}
              </Button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
